import { CheckCircle2, MessageSquareText, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Input,
  Modal,
  OtpInput,
  Spinner,
} from "@/components/common";
import {
  useCancelConsentRequest,
  useConsentRequest,
  useRequestConsent,
  useVerifyConsentCodes,
} from "@/hooks/useClients";
import type { ConsentRequest } from "@/types/clients";
import { formatDateTime } from "./RodoStatusBadges";

interface ConsentRequestModalProps {
  requestId: string | null;
  clientName: string;
  onClose: () => void;
}

const digitsOnly = (v: string) => v.replace(/\D/g, "").slice(0, 6);

function useSecondsLeft(expiresAt: string | undefined) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, []);
  if (!expiresAt) {
    return 0;
  }
  return Math.max(0, Math.floor((new Date(expiresAt).getTime() - now) / 1000));
}

function Decisions({ request }: { request: ConsentRequest }) {
  const marketing =
    request.marketingPhone || request.marketingSms || request.marketingEmail;
  const channels = [
    request.marketingPhone && "telefon",
    request.marketingSms && "SMS",
    request.marketingEmail && "e-mail",
  ].filter(Boolean);
  const rows: [string, boolean][] = [
    ["Klauzula informacyjna — potwierdzona", true],
    ["Baza klientów", request.clientBase],
    [marketing ? `Marketing (${channels.join(", ")})` : "Marketing", marketing],
  ];
  if (request.health !== null) {
    rows.push(["Dane o zdrowiu", request.health]);
  }
  return (
    <ul className="divide-y divide-edge-soft rounded-lg border border-edge-soft">
      {rows.map(([label, granted]) => (
        <li
          key={label}
          className="flex items-center justify-between px-4 py-2.5 text-sm"
        >
          <span className="text-main">{label}</span>
          <Badge variant={granted ? "success" : "default"}>
            {granted ? "TAK" : "NIE"}
          </Badge>
        </li>
      ))}
    </ul>
  );
}

export function ConsentRequestModal({
  requestId: initialRequestId,
  clientName,
  onClose,
}: ConsentRequestModalProps) {
  const [requestId, setRequestId] = useState(initialRequestId);
  useEffect(() => {
    setRequestId(initialRequestId);
  }, [initialRequestId]);

  const { data: request, isLoading } = useConsentRequest(requestId);
  const verify = useVerifyConsentCodes();
  const cancel = useCancelConsentRequest();
  const resend = useRequestConsent();

  const [confirmationCode, setConfirmationCode] = useState("");
  const [consentCodes, setConsentCodes] = useState(["", "", ""]);
  const secondsLeft = useSecondsLeft(request?.expiresAt);

  function resetInputs() {
    setConfirmationCode("");
    setConsentCodes(["", "", ""]);
    verify.reset();
  }

  function handleClose() {
    resetInputs();
    resend.reset();
    onClose();
  }

  function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    if (!requestId) {
      return;
    }
    const codes = [confirmationCode, ...consentCodes].filter(
      (c) => c.length === 6,
    );
    verify.mutate({ requestId, codes });
  }

  function handleResend() {
    if (!request) {
      return;
    }
    resend.mutate(
      {
        clientId: request.clientId,
        includesHealth: request.includesHealth,
        channel: request.channel,
      },
      {
        onSuccess: (newRequest) => {
          resetInputs();
          setRequestId(newRequest.id);
        },
      },
    );
  }

  function handleCancel() {
    if (requestId) {
      cancel.mutate(requestId, { onSuccess: handleClose });
    }
  }

  const consentSlots = request?.includesHealth ? 3 : 2;
  const isOpenRequest = request?.status === "Pending" && secondsLeft > 0;
  // The client has 12 hours, so the countdown shows h:mm:ss.
  const hours = Math.floor(secondsLeft / 3600);
  const minutes = String(Math.floor((secondsLeft % 3600) / 60)).padStart(
    2,
    "0",
  );
  const seconds = String(secondsLeft % 60).padStart(2, "0");

  return (
    <Modal
      isOpen={initialRequestId !== null}
      onClose={handleClose}
      title={`Zgoda RODO — ${clientName}`}
    >
      {isLoading || !request ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : request.status === "Confirmed" ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3 rounded-lg bg-green-50 p-4 text-green-800 dark:bg-green-900/20 dark:text-green-300">
            <CheckCircle2 size={28} aria-hidden />
            <div>
              <p className="font-semibold">RODO zweryfikowane</p>
              <p className="text-sm">
                {request.confirmedAt && formatDateTime(request.confirmedAt)} ·{" "}
                {request.method === "Link"
                  ? "klient potwierdził przez link"
                  : "kody SMS"}{" "}
                · wersja klauzuli {request.clauseVersion}
              </p>
            </div>
          </div>
          <Decisions request={request} />
          <p className="text-sm text-sub">
            {request.channel === "Sms"
              ? "Klient bez e-maila nie dostaje potwierdzenia SMS-em — swoje decyzje widzi pod linkiem z SMS-a."
              : "Klient otrzymał potwierdzenie e-mailem."}
          </p>
          <div className="flex justify-end border-t border-edge-soft pt-4">
            <Button onClick={handleClose}>Gotowe</Button>
          </div>
        </div>
      ) : isOpenRequest ? (
        <form onSubmit={handleVerify} className="space-y-5">
          <div className="flex items-start gap-3 rounded-lg bg-subtle p-4">
            <MessageSquareText
              size={20}
              className="mt-0.5 shrink-0 text-adara-600"
              aria-hidden
            />
            <div className="text-sm text-sub">
              <p>
                {request.channel === "Email" ? (
                  <>
                    E-mail z kodami wysłany na <strong>{request.email}</strong>.
                  </>
                ) : (
                  <>
                    SMS z kodami wysłany na{" "}
                    <strong>{request.phoneMasked}</strong>
                    {request.channel === "SmsAndEmail" && request.email && (
                      <>
                        {" "}
                        i e-mail na <strong>{request.email}</strong>
                      </>
                    )}
                    .
                  </>
                )}
              </p>
              <p className="mt-1">
                Link i kody wygasną za{" "}
                <strong>
                  {hours}:{minutes}:{seconds}
                </strong>
                . Klient może też potwierdzić sam przez link — ten ekran
                odświeży się automatycznie.
              </p>
            </div>
          </div>

          {verify.isError && (
            <Alert variant="error">
              {(verify.error as { message?: string }).message ??
                "Nie udało się zweryfikować kodów"}
            </Alert>
          )}

          <div>
            <p className="mb-2 text-sm font-medium text-main">
              Kod POTWIERDZENIA (wymagany)
            </p>
            <OtpInput value={confirmationCode} onChange={setConfirmationCode} />
          </div>

          <div>
            <p className="mb-1 text-sm font-medium text-main">
              Kody zgód — tylko te, które klient podał
            </p>
            <p className="mb-2 text-xs text-faint">
              Zgoda bez podanego kodu zostanie zapisana jako NIE. Nie pytaj o
              konkretne zgody — poproś o odczytanie kodów, na które klient się
              zgadza.
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              {consentCodes.slice(0, consentSlots).map((code, i) => (
                <Input
                  // biome-ignore lint/suspicious/noArrayIndexKey: fixed-size list of slots
                  key={i}
                  aria-label={`Kod zgody ${i + 1}`}
                  value={code}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  onChange={(e) => {
                    const next = [...consentCodes];
                    next[i] = digitsOnly(e.target.value);
                    setConsentCodes(next);
                  }}
                />
              ))}
            </div>
          </div>

          <p className="text-xs text-faint">
            Pozostało prób: {request.attemptsLeft}
          </p>

          <div className="flex flex-wrap justify-between gap-3 border-t border-edge-soft pt-4">
            <Button
              variant="ghost"
              type="button"
              onClick={handleCancel}
              isLoading={cancel.isPending}
            >
              Anuluj wniosek
            </Button>
            <Button
              type="submit"
              isLoading={verify.isPending}
              disabled={confirmationCode.length !== 6}
            >
              Zatwierdź kody
            </Button>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <Alert variant="warning">
            {request.status === "Locked"
              ? "Wniosek zablokowany po zbyt wielu błędnych kodach."
              : request.status === "Cancelled"
                ? "Wniosek został anulowany."
                : "Link i kody wygasły."}{" "}
            Wyślij wniosek ponownie, aby kontynuować.
          </Alert>
          {resend.isError && (
            <Alert variant="error">
              {(resend.error as { message?: string }).message ??
                "Nie udało się wysłać nowego wniosku"}
            </Alert>
          )}
          <div className="flex justify-end gap-3 border-t border-edge-soft pt-4">
            <Button variant="ghost" onClick={handleClose}>
              Zamknij
            </Button>
            <Button
              onClick={handleResend}
              isLoading={resend.isPending}
              leftIcon={<RefreshCw size={16} aria-hidden />}
            >
              Wyślij ponownie
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
